import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Search, X, Sparkles, ChevronRight } from "lucide-react";
import { SPELL_EFFECTS, SPELL_SCHOOLS } from "@/data/spellEffects";

/**
 * Slide-out Spell Browser — matches Token / Asset browser pattern.
 * Lets the DM pick a spell to cast on the map and manage active effects.
 */
export const SpellBrowserPanel = ({
  open,
  onClose,
  castingSpell,
  setCastingSpell,
  spellEffects = [],
  removeSpellEffect,
  clearSpellEffects,
  setSelectedSpellId,
  setSpellRotation,
  onSpellDragStart,
  onSpellDragEnd,
  width: initialWidth = 320,
}) => {
  const [search, setSearch] = useState("");
  const [width, setWidth] = useState(() => parseInt(localStorage.getItem("vtt_spell_browser_w"), 10) || initialWidth);

  const startResize = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = width;
    const move = (ev) => {
      const w = Math.max(260, Math.min(560, startW + (ev.clientX - startX)));
      setWidth(w);
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      localStorage.setItem("vtt_spell_browser_w", String(width));
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const groupedSpells = useMemo(() => {
    const q = search.trim().toLowerCase();
    return Object.entries(SPELL_SCHOOLS).map(([key, school]) => {
      const spells = SPELL_EFFECTS.filter(s => s.school === key && (!q || s.name.toLowerCase().includes(q)));
      return { key, school, spells };
    }).filter(g => g.spells.length > 0);
  }, [search]);

  if (!open) return null;

  return (
    <div
      className="absolute top-12 left-14 z-30 bg-[#0f0f12]/95 backdrop-blur-md border border-[#333] rounded-r-lg shadow-2xl flex flex-col"
      style={{ width, height: "calc(100% - 4rem)" }}
      onWheel={(e) => e.stopPropagation()}
      data-vtt-sidebar="spells"
      data-testid="spell-browser-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#333] bg-gradient-to-b from-[#1a1a22] to-[#101015] rounded-tr-lg">
        <h3 className="text-sm font-bold text-white flex items-center gap-1.5">
          <Sparkles className="w-4 h-4 text-primary" /> Spell Browser
        </h3>
        <button onClick={onClose} className="text-[#888] hover:text-white" data-testid="close-spell-browser">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Search */}
      <div className="p-2 border-b border-[#333]">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#666]" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search spells…"
            className="h-8 text-sm pl-7 bg-[#1a1a1a] border-[#333] text-white"
            data-testid="sb-search"
          />
        </div>
      </div>

      {/* Casting indicator */}
      {castingSpell && (
        <div className="mx-2 mt-2 p-2 bg-primary/15 border border-primary/40 rounded-sm">
          <div className="flex items-center justify-between">
            <span className="text-sm text-primary font-medium truncate">Casting: {castingSpell.name}</span>
            <button
              onClick={() => { setCastingSpell?.(null); setSpellRotation?.(0); }}
              className="text-[#888] hover:text-white"
              data-testid="sb-cancel-cast"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <p className="text-xs text-[#aaa] mt-1 leading-snug">
            Click on the map to place.
            {castingSpell.shape === "cone" || castingSpell.shape === "line" ? " Scroll to rotate." : ""}
          </p>
        </div>
      )}

      {/* Active spell effects */}
      {spellEffects.length > 0 && (
        <div className="px-2 pt-2 pb-1 border-b border-[#222]">
          <p className="text-xs text-[#888] uppercase tracking-wider mb-1">Active Effects ({spellEffects.length})</p>
          <div className="max-h-32 overflow-y-auto space-y-0.5">
            {spellEffects.map(se => (
              <div key={se.id} className="flex items-center gap-1.5 py-1 px-1 rounded-sm hover:bg-[#222] group">
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: SPELL_SCHOOLS[se.spell.school]?.color }} />
                <span className="text-sm text-white truncate flex-1">{se.spell.name}</span>
                <button
                  className="text-[#666] hover:text-red-400 opacity-60 group-hover:opacity-100"
                  onClick={() => removeSpellEffect?.(se.id)}
                  data-testid={`sb-remove-effect-${se.id}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
          <button
            className="text-xs text-red-400 hover:text-red-300 mt-1.5"
            onClick={clearSpellEffects}
            data-testid="sb-clear-effects"
          >Clear all effects</button>
        </div>
      )}

      {/* Spell list grouped by school */}
      <div className="flex-1 overflow-y-auto px-2 py-2 space-y-1" data-testid="sb-list">
        {groupedSpells.length === 0 ? (
          <p className="p-4 text-sm text-[#666] italic text-center">No matching spells.</p>
        ) : groupedSpells.map(({ key, school, spells }) => (
          <details key={key} open className="group/school">
            <summary className="flex items-center gap-1.5 py-1 px-1 cursor-pointer rounded-sm hover:bg-[#1a1a22] text-white select-none list-none">
              <ChevronRight className="w-3 h-3 group-open/school:rotate-90 transition-transform" />
              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: school.color }} />
              <span className="text-sm font-medium uppercase tracking-wider flex-1">{school.label}</span>
              <span className="text-xs text-[#666]">{spells.length}</span>
            </summary>
            <div className="ml-5 mt-0.5 space-y-0.5">
              {spells.map(spell => {
                const active = castingSpell?.name === spell.name;
                return (
                  <div
                    key={spell.name}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData("application/x-natone-spell", JSON.stringify(spell));
                      e.dataTransfer.effectAllowed = "copy";
                      setSpellRotation?.(0);
                      onSpellDragStart?.(spell);
                    }}
                    onDragEnd={() => { onSpellDragEnd?.(); }}
                    className={`w-full flex items-center gap-2 py-1 px-2 rounded-sm hover:bg-[#222] cursor-grab active:cursor-grabbing transition-colors ${active ? "bg-primary/15 ring-1 ring-primary/30" : ""}`}
                    title="Drag onto the map to cast"
                    data-testid={`sb-cast-${spell.name.replace(/\s/g, "-").toLowerCase()}`}
                  >
                    <span className="text-sm text-white truncate flex-1 select-none">{spell.name}</span>
                    <span className="text-xs text-[#888] uppercase select-none">{spell.shape}</span>
                    <span className="text-xs text-[#666] select-none">Lv{spell.level}</span>
                  </div>
                );
              })}
            </div>
          </details>
        ))}
      </div>

      {/* Footer hint */}
      <div className="px-3 py-1.5 border-t border-[#333] bg-[#0a0a0d] text-xs text-[#666]">
        Drag a spell onto the map to place it · Scroll while dragging to rotate cone/line.
      </div>

      {/* Resize handle */}
      <div onPointerDown={startResize} className="absolute top-0 right-0 w-1.5 h-full cursor-ew-resize bg-transparent hover:bg-primary/30" data-testid="sb-resize" />
    </div>
  );
};

export default SpellBrowserPanel;
